<script>
import ContentBox from '@/components/ui/ContentBox';
import ImageInput from '@/components/form-controls/ImageInput';
import LanguageSelect from '@/components/i18n/LanguageSelect';
import PermissionCard from '@/components/users/PermissionCard';
import animate from '@/modules/animate-css';
import uuid from 'uuid/v4';

export default {
  name: 'UserForm',
  components: {
    ContentBox,
    ImageInput,
    LanguageSelect,
    PermissionCard
  },
  props: {
    data: Object,
    deleting: Boolean,
    disabled: Boolean,
    invalid: Array,
    isNew: Boolean,
    isProfile: Boolean,
    saving: Boolean
  },
  data() {
    return {
      activeTab: 'general',
      cleanState: {},
      form: {
        name: '',
        email: '',
        avatar: null,
        bio: '',
        isAdmin: false,
        canManageBilling: false,
        canManageSettings: false,
        permissions: [],
        currentPassword: '',
        password: ''
      },
      permissionSearch: '',
      showCurrentPassword: false,
      showPassword: false,
      sites: []
    };
  },
  created() {
    // Set the initial data
    if (this.data) {
      this.setData(this.data);
    }

    // Set the initial clean state
    this.resetCleanState();
  },
  methods: {
    // Gets a list of sites for autocomplete
    getSites(search) {
      this.$http.get('/sites', {
        params: {
          search,
          limit: 20,
          sortBy: 'name'
        }
      })
        .then(res => this.sites = res.data.sites)
        .catch(() => this.sites = []);
    },

    // Look for a gravatar when the email changes
    handleEmailChange() {
      // If an avatar isn't set OR if it's set to a gravatar, update it
      if (
        this.form.email.length &&
        (!this.form.avatar || this.form.avatar.indexOf('gravatar.com') > -1)
      ) {
        this.$http.get('/utilities/gravatar', {
          params: {
            email: this.form.email,
            size: 512,
            verify: true
          }
        })
          .then(res => {
            if (res.data.url) {
              this.form.avatar = res.data.url;
            }
          })
          .catch(() => { /* do nothing */ });
      }
    },

    // Called when the isAdmin control is changed
    handleIsAdminChange() {
      if (!this.isAdmin) {
        this.form.canManageBilling = false;
        this.form.canManageSettings = false;
      }
    },

    // Adds a permission
    addPermission(site) {
      const permission = {
        id: uuid(),
        userId: this.$route.params.userId,
        siteId: site.id,
        site,
        pages: null
      };
      const duplicate = this.form.permissions.filter(p => p.siteId === permission.siteId);

      // Prepend the permission
      if (duplicate.length) {
        const el = this.$el.querySelector(`.permission-card[data-id="${duplicate[0].id}"]`);
        animate(el, 'bounce', 1);
      } else {
        this.form.permissions.unshift(permission);
      }

      // Reset the control
      this.permissionSearch = '';
    },

    // Updates a permission
    updatePermission(permissionId, pages) {
      this.form.permissions = this.form.permissions.map(p => {
        if (p.id === permissionId) {
          p.pages = Array.isArray(pages) ? pages : null;
        }

        return p;
      });
    },

    // Revokes a permission
    revokePermission(permission) {
      this.form.permissions = this.form.permissions.filter(p => p.id !== permission.id);
    },

    // Returns the form's data
    getData() {
      return Object.assign({}, this.form);
    },

    // Sets the form's data
    setData(data) {
      Object.keys(this.form).forEach(key => {
        this.form[key] = typeof data[key] === 'undefined' ? this.form[key] : data[key];
      });
    },

    // Tells if the current state is different than the clean state
    isDirty() {
      return JSON.stringify(this.cleanState) !== JSON.stringify(this.form);
    },

    // Sets the clean state to the current state
    resetCleanState() {
      this.cleanState = Object.assign({}, this.form);
    }
  }
};
</script>

<template>
  <el-form
    ref="userForm"
    :disabled="disabled"
    class="user-form"
    label-position="top"
    @submit.native.prevent="$emit('submit')"
  >
    <content-box>
      <el-tabs v-model="activeTab">
        <!-- General -->
        <el-tab-pane :label="$t('General')" name="general">
          <fieldset>
            <!-- Avatar -->
            <el-form-item class="text-center">
              <image-input
                v-model="form.avatar"
                :value="form.avatar"
                :default-image="require('@/assets/user-light.svg')"
                :title="$t('Change Profile Picture')"
                :height="128"
                class="avatar-input"
                mime-type="image/jpeg"
                crop
              />
            </el-form-item>

            <!-- Name -->
            <el-form-item
              :label="$t('Name')"
              :class="{ 'is-error': invalid.includes('name') }"
            >
              <el-input
                ref="name"
                v-model="form.name"
                :class="{ 'is-error': invalid.includes('name') }"
                type="text"
                auto-complete="off"
              />
            </el-form-item>

            <el-row :gutter="20">
              <el-col :xs="24" :sm="24" :md="isProfile ? 12 : 24">
                <!-- Email -->
                <el-form-item
                  :label="$t('Email')"
                  :class="{ 'is-error': invalid.includes('email') }"
                >
                  <el-input
                    v-model="form.email"
                    :class="{ 'is-error': invalid.includes('email') }"
                    type="email"
                    auto-complete="off"
                    @change="handleEmailChange"
                  />
                  <div v-if="isNew" class="text-form-description">
                    {{ $t('An invitation will be sent to this email address when the user is created.') }}
                  </div>
                </el-form-item>
              </el-col>
              <el-col v-if="isProfile" :xs="24" :sm="24" :md="isProfile ? 12 : 24">
                <!-- Language picker -->
                <el-form-item :label="$t('Language')">
                  <language-select />
                </el-form-item>
              </el-col>
            </el-row>

            <!-- Bio -->
            <el-form-item
              :label="$t('Bio')"
              :class="{ 'is-error': invalid.includes('bio') }"
            >
              <el-input
                v-model="form.bio"
                :autosize="{ minRows: 4, maxRows: 4 }"
                :rows="4"
                type="textarea"
                max-rows="4"
                auto-complete="off"
              />
              <div class="text-form-description">
                <span v-if="isProfile">
                  {{ $t('Type a few sentences about yourself.') }}
                </span>
                <span v-else>
                  {{ $t('Type a few sentences about this user.') }}
                </span>
              </div>
            </el-form-item>
          </fieldset>
        </el-tab-pane>

        <!-- Permissions -->
        <el-tab-pane v-if="!isProfile" :label="$t('Permissions')" name="permissions">
          <el-alert
            v-if="form.isAdmin"
            :title="form.name"
            :description="$t('This user is an administrator so they have access to all sites. You need to disable administrative privileges if you want to limit their access.')"
            :closable="false"
            type="info"
          />
          <div v-else>
            <el-form-item
              :label="$t('This user will have access to the following sites.')"
            >
              <el-select
                v-model="permissionSearch"
                :class="{ 'is-error': invalid.includes('url') }"
                :debounce="300"
                :remote-method="getSites"
                :loading-text="$t('Loading...')"
                :no-data-text="$t('No Results')"
                :placeholder="$t('Start typing to add a site...')"
                filterable
                remote
                @change="addPermission"
              >
                <el-option
                  v-for="site in sites"
                  :key="site.id"
                  :label="site.name"
                  :value="site"
                />
              </el-select>
            </el-form-item>

            <!-- Permission cards -->
            <div ref="permissionCards" class="permission-cards">
              <permission-card
                v-for="permission in form.permissions"
                :key="permission.id"
                :permission="permission"
                @revoke="revokePermission(permission)"
                @update="updatePermission(permission.id, $event)"
              />
            </div>
          </div>
        </el-tab-pane>

        <!-- Advanced -->
        <el-tab-pane v-if="!isProfile" :label="$t('Advanced')" name="advanced">
          <fieldset>
            <!-- Make admin -->
            <el-form-item>
              <el-switch
                v-model="form.isAdmin"
                :checked="form.isAdmin"
                :active-text="$t('Make Administrator')"
                auto-complete="off"
                @change="handleIsAdminChange"
              />
              <div class="text-form-description">
                {{ $t('Allow this user to manage all sites and users in this account.') }}
              </div>
            </el-form-item>

            <!-- Make admin -->
            <el-form-item>
              <el-switch
                v-model="form.canManageSettings"
                :checked="form.canManageSettings"
                :active-text="$t('Manage Settings')"
                :disabled="!form.isAdmin"
                auto-complete="off"
              />
              <div class="text-form-description">
                {{ $t('Allow this user to change the settings for this account.') }}
              </div>
            </el-form-item>

            <!-- Make admin -->
            <el-form-item>
              <el-switch
                v-model="form.canManageBilling"
                :checked="form.canManageBilling"
                :active-text="$t('Manage Billing')"
                :disabled="!form.isAdmin"
                auto-complete="off"
              />
              <div class="text-form-description">
                {{ $t('Allow this user to view invoices and manage payment info.') }}
              </div>
            </el-form-item>
          </fieldset>

          <fieldset v-if="!isNew" class="divided">
            <!-- Delete -->
            <h2>{{ $t('Delete') }}</h2>
            <p>
              {{ $t('This will delete the user and revoke all access to the CMS.') }}
            </p>
            <el-form-item>
              <el-button :loading="deleting" type="danger" @click="$emit('delete')">
                {{ $t('Delete This User') }}
              </el-button>
            </el-form-item>
          </fieldset>
        </el-tab-pane>

        <!-- Change password -->
        <el-tab-pane v-if="isProfile" :label="$t('Change Password')" name="advanced">
          <!-- Current password -->
          <el-form-item :label="$t('Current Password')" :class="{ 'is-error': invalid.includes('currentPassword') }">
            <el-input
              v-model="form.currentPassword"
              :type="showCurrentPassword ? 'text' : 'password'"
              auto-complete="off"
            >
              <a slot="suffix" href="#" @click.prevent="showCurrentPassword = !showCurrentPassword">
                <i :class="{
                  'fal': true,
                  'el-icon-fa-eye': !showCurrentPassword,
                  'el-icon-fa-eye-slash': showCurrentPassword
                }" />
              </a>
            </el-input>
          </el-form-item>

          <!-- New password -->
          <el-form-item :label="$t('New Password')" :class="{ 'is-error': invalid.includes('password') }">
            <el-input
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              auto-complete="off"
            >
              <a slot="suffix" href="#" @click.prevent="showPassword = !showPassword">
                <i :class="{
                  'fal': true,
                  'el-icon-fa-eye': !showPassword,
                  'el-icon-fa-eye-slash': showPassword
                }" />
              </a>
            </el-input>
            <div class="text-form-description">
              {{ $t('Passwords must be at least eight characters long.') }}
            </div>
          </el-form-item>
        </el-tab-pane>
      </el-tabs>
    </content-box>

    <div class="text-right">
      <!-- Cancel -->
      <el-button v-if="!isProfile" @click="$emit('cancel')">
        {{ $t('Cancel') }}
      </el-button>

      <!-- Submit -->
      <el-button :loading="saving" type="primary" native-type="submit">
        <span v-if="isNew">{{ $t('Create User') }}</span>
        <span v-else>{{ $t('Save Changes') }}</span>
      </el-button>
    </div>
  </el-form>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.avatar-input {
  width: 128px;
  margin: 0 auto;

  /deep/ {
    .image-input-button {
      padding: 1px;
    }

    .image-input-button,
    .image-input-image {
      width: 128px;
      border-radius: 50%;
    }
  }
}
</style>
